import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Edit, Clock, FileSignature, CheckCircle, PauseCircle, Ban, XCircle, Download, Shield, Award, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function useGestionConventions() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [conventions, setContrats] = useState([]);
  const [filteredConventionsList, setFilteredConventionsList] = useState([]);
  const [coopersList, setCoopersList] = useState([]);
  const [expertsList, setExpertsList] = useState([]);
  const [stats, setStats] = useState(null);
  const [_activeTab, _setActiveTab] = useState('tous');
  
  // Filtres (gardés pour compatibilité mais le nouveau composant gère ses propres filtres)
  const [searchQuery, _setSearchQuery] = useState('');
  const [typeFilter, _setTypeFilter] = useState('all');
  const [statutFilter, _setStatutFilter] = useState('all');
  const [useAdvancedFilters, _setUseAdvancedFilters] = useState(true);
  
  // Sélection en masse
  const [selectedConventions, setSelectedConventions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEnvoiModal, setOpenEnvoiModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [openActionMasseModal, setOpenActionMasseModal] = useState(false);
  const [openPaymentShareModal, setOpenPaymentShareModal] = useState(false);
  
  // Mode édition
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  
  // États
  const [selectedConvention, setSelectedContrat] = useState(null);
  const [formData, setFormData] = useState({
    type_convention: 'cooper',
    titre: '',
    contenu_html: '',
    duree_mois: 12,
    conditions_specifiques: '',
    montant_mensuel: null,
    destinataire_autre: {
      entreprise: '',
      siret: '',
      code_naf: '',
      email: '',
      nom: '',
      prenom: ''
    }
  });
  
  const [envoiData, setEnvoiData] = useState({
    destinataire_id: '',
    message_accompagnement: ''
  });
  
  // État pour création de nouveau destinataire par email
  const [showCreateRecipient, setShowCreateRecipient] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientPrenom, setNewRecipientPrenom] = useState('');
  const [newRecipientNom, setNewRecipientNom] = useState('');
  const [newRecipientEntreprise, setNewRecipientEntreprise] = useState('');
  const [creatingRecipient, setCreatingRecipient] = useState(false);
  
  const [actionData, setActionData] = useState({
    action: '',
    raison: ''
  });
  
  const [actionMasseData, setActionMasseData] = useState({
    action: '',
    raison: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conventionsRes, statsRes, coopersRes, expertsRes] = await Promise.all([
        axios.get(`${API}/admin/conventions`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/conventions/statistiques`, { headers: getAuthHeader() }),
        axios.get(`${API}/coopers`, { headers: getAuthHeader() }),
        axios.get(`${API}/experts`, { headers: getAuthHeader() })
      ]);
      
      // Trier les conventions du plus récent au plus ancien
      const sortedConventions = (conventionsRes.data.conventions || []).sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Ordre décroissant (plus récent en premier)
      });
      setContrats(sortedConventions);
      setStats(statsRes.data);
      setCoopersList(coopersRes.data?.coopers || coopersRes.data || []);
      setExpertsList(expertsRes.data?.experts || expertsRes.data || []);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConvention = async () => {
    // Validation des champs obligatoires
    if (!formData.titre || !formData.titre.trim()) {
      toast.error('Le titre de la convention est obligatoire');
      return;
    }
    
    if (!formData.contenu_html || !formData.contenu_html.trim()) {
      toast.error('Le contenu de la convention est obligatoire');
      return;
    }
    
    // Validation pour type "autre"
    if (formData.type_convention === 'autre') {
      if (!formData.destinataire_autre.prenom || !formData.destinataire_autre.nom || !formData.destinataire_autre.email) {
        toast.error('Veuillez remplir les champs obligatoires (Prénom, Nom, Email)');
        return;
      }
    }
    
    try {
      await axios.post(`${API}/admin/conventions`, formData, { headers: getAuthHeader() });
      toast.success('Convention créé avec succès ✅');
      setOpenCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur lors de la création : ${errorMsg}`);
    }
  };

  // Ouvrir le mode édition
  const handleOpenEdit = (convention) => {
    setEditFormData({
      titre: convention.titre || '',
      contenu_html: convention.contenu_html || '',
      duree_mois: convention.duree_mois || 12,
      conditions_specifiques: convention.conditions_specifiques || '',
      montant_mensuel: convention.montant_mensuel || null,
      destinataire_autre: convention.destinataire_autre || {
        entreprise: '',
        siret: '',
        code_naf: '',
        email: '',
        nom: '',
        prenom: ''
      }
    });
    setIsEditing(true);
  };

  // Sauvegarder les modifications
  const handleSaveEdit = async () => {
    if (!editFormData.titre || !editFormData.contenu_html) {
      toast.error('Le titre et le contenu sont obligatoires');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/conventions/${selectedConvention.id}`,
        editFormData,
        { headers: getAuthHeader() }
      );
      toast.success('Convention mis à jour avec succès ✅');
      setIsEditing(false);
      fetchData();
      // Mettre à jour la convention sélectionnée
      setSelectedContrat(prev => ({ ...prev, ...editFormData }));
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData(null);
  };

  const handleEnvoyerConvention = async () => {
    // Pour type "autre", pas besoin de destinataire_id
    if (selectedConvention.type_convention !== 'autre' && selectedConvention.type_contrat !== 'autre' && !envoiData.destinataire_id) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }
    
    try {
      // Notification d'envoi en cours
      toast.loading('📤 Envoi de la convention en cours...', { id: 'envoi-convention' });
      
      const response = await axios.post(
        `${API}/admin/conventions/${selectedConvention.id}/envoyer`,
        envoiData,
        { headers: getAuthHeader() }
      );
      
      // Fermer le toast de chargement
      toast.dismiss('envoi-convention');
      
      // Afficher les notifications selon le statut de l'email
      const emailStatus = response.data.email_status;
      const destinataire = response.data.destinataire || 'destinataire';
      
      if (emailStatus === 'sent') {
        toast.success(`✅ Convention envoyée avec succès à ${destinataire}`, {
          description: '📧 Email de notification envoyé',
          duration: 5000
        });
        // Notification d'accusé de réception
        setTimeout(() => {
          toast.success(`📬 Accusé de réception confirmé`, {
            description: `L'email a bien été délivré à ${destinataire}`,
            duration: 4000
          });
        }, 1500);
      } else if (emailStatus === 'simulated') {
        toast.success(`✅ Convention envoyée avec succès à ${destinataire}`, {
          description: '📧 Email simulé (mode développement)',
          duration: 5000
        });
      } else if (emailStatus === 'failed' || emailStatus === 'warning') {
        toast.warning(`⚠️ Convention envoyée mais problème d'email`, {
          description: response.data.email_message || 'Vérifiez la configuration email',
          duration: 6000
        });
      } else {
        toast.success(`✅ Convention envoyée avec succès à ${destinataire}`);
      }
      
      setOpenEnvoiModal(false);
      resetEnvoiData();
      fetchData();
    } catch (error) {
      toast.dismiss('envoi-convention');
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      
      // Notification d'échec
      toast.error(`❌ Échec de l'envoi`, {
        description: errorMsg,
        duration: 6000
      });
    }
  };

  // Créer un nouveau destinataire par email
  const handleCreateRecipientByEmail = async () => {
    if (!newRecipientEmail || !newRecipientEmail.includes('@')) {
      toast.error('Veuillez saisir un email valide');
      return;
    }
    
    setCreatingRecipient(true);
    try {
      const role = (selectedConvention?.type_convention === 'cooper' || selectedConvention?.type_contrat === 'cooper') 
        ? 'cooper' 
        : 'expert';
      
      const response = await axios.post(
        `${API}/admin/conventions/create-recipient-by-email`,
        {
          email: newRecipientEmail,
          prenom: newRecipientPrenom || null,
          nom: newRecipientNom || null,
          entreprise: newRecipientEntreprise || null,
          role: role
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.success) {
        const roleLabel = role === 'cooper' ? "COOPER'S" : "EXPERT";
        
        if (response.data.already_exists) {
          toast.success(`✅ Utilisateur existant sélectionné`, {
            description: `${response.data.nom_complet} - ${response.data.email}`
          });
        } else {
          toast.success(`✅ Nouveau ${roleLabel} créé et invitation envoyée !`, {
            description: `${response.data.email}`,
            duration: 5000
          });
        }
        
        // Sélectionner automatiquement ce destinataire
        setEnvoiData({ ...envoiData, destinataire_id: response.data.user_id });
        
        // Rafraîchir la liste des destinataires
        fetchData();
        
        // Fermer le formulaire de création
        setShowCreateRecipient(false);
        setNewRecipientEmail('');
        setNewRecipientPrenom('');
        setNewRecipientNom('');
        setNewRecipientEntreprise('');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de la création';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setCreatingRecipient(false);
    }
  };

  const handleAction = async () => {
    if (!actionData.raison) {
      toast.error('Veuillez indiquer la raison');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/conventions/${selectedConvention.id}/action`,
        actionData,
        { headers: getAuthHeader() }
      );
      
      const messages = {
        suspendre: 'Convention suspendu',
        reactiver: 'Convention réactivé ✅',
        resilier: 'Convention résilié'
      };
      
      toast.success(messages[actionData.action]);
      setOpenActionModal(false);
      resetActionData();
      fetchData();
    } catch {
      toast.error('Erreur lors de l\'action');
    }
  };

  const handleDelete = async (conventionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce brouillon ?')) return;
    
    try {
      await axios.delete(`${API}/admin/conventions/${conventionId}`, { headers: getAuthHeader() });
      toast.success('Convention supprimé');
      fetchData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Dupliquer une convention existante
  const handleDuplicateConvention = async (conventionId) => {
    try {
      const response = await axios.post(
        `${API}/admin/conventions/${conventionId}/duplicate`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Convention dupliquée avec succès ! 📋');
      fetchData();
      // Ouvrir le nouveau brouillon pour édition
      if (response.data.convention) {
        setSelectedContrat(response.data.convention);
        handleOpenEdit(response.data.convention);
        setOpenDetailModal(true);
      }
    } catch {
      toast.error('Erreur lors de la duplication de la convention');
    }
  };

  // Callback pour l'import Word
  const _handleWordImport = (htmlContent, fileName) => {
    setFormData(prev => ({
      ...prev,
      contenu_html: htmlContent,
      titre: prev.titre || fileName.replace(/\.(docx|doc)$/i, '')
    }));
    toast.success(`Contenu importé depuis ${fileName}`);
  };

  const resetForm = () => {
    setFormData({
      type_convention: 'cooper',
      titre: '',
      contenu_html: '',
      duree_mois: 12,
      conditions_specifiques: '',
      montant_mensuel: null,
      destinataire_autre: {
        entreprise: '',
        siret: '',
        code_naf: '',
        email: '',
        nom: '',
        prenom: ''
      }
    });
  };

  const resetEnvoiData = () => {
    setEnvoiData({
      destinataire_id: '',
      message_accompagnement: ''
    });
  };

  const resetActionData = () => {
    setActionData({
      action: '',
      raison: ''
    });
  };
  
  const resetActionMasseData = () => {
    setActionMasseData({
      action: '',
      raison: ''
    });
  };
  
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedConventions([]);
      setSelectAll(false);
    } else {
      setSelectedConventions(filteredConventions.map(c => c.id));
      setSelectAll(true);
    }
  };
  
  const handleSelectConvention = (conventionId) => {
    if (selectedConventions.includes(conventionId)) {
      setSelectedConventions(selectedConventions.filter(id => id !== conventionId));
      setSelectAll(false);
    } else {
      const newSelection = [...selectedConventions, conventionId];
      setSelectedConventions(newSelection);
      if (newSelection.length === filteredConventions.length) {
        setSelectAll(true);
      }
    }
  };
  
  const handleActionMasse = async () => {
    if (!actionMasseData.action) {
      toast.error('Veuillez sélectionner une action');
      return;
    }
    
    // Vérifier si raison est requise
    const actionsNecessitantRaison = ['suspendre', 'resilier', 'archiver'];
    if (actionsNecessitantRaison.includes(actionMasseData.action) && !actionMasseData.raison) {
      toast.error('Veuillez indiquer la raison');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/conventions/actions-masse`,
        {
          contrat_ids: selectedConventions,
          action: actionMasseData.action,
          raison: actionMasseData.raison
        },
        { headers: getAuthHeader() }
      );
      
      const { resultats } = response.data;
      
      if (resultats.succes.length > 0) {
        toast.success(`✅ ${resultats.succes.length} convention(s) traité(s) avec succès`);
      }
      
      if (resultats.erreurs.length > 0) {
        toast.error(`❌ ${resultats.erreurs.length} erreur(s) lors du traitement`);
      }
      
      setOpenActionMasseModal(false);
      resetActionMasseData();
      setSelectedConventions([]);
      setSelectAll(false);
      fetchData();
    } catch {
      toast.error('Erreur lors de l\'action en masse');
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { bg: 'bg-gray-800', icon: <Edit className="h-3 w-3" />, label: 'Brouillon' },
      en_attente_signature: { bg: 'bg-amber-700', icon: <Clock className="h-3 w-3" />, label: 'En attente' },
      signe: { bg: 'bg-blue-800', icon: <FileSignature className="h-3 w-3" />, label: 'Signé' },
      actif: { bg: 'bg-green-800', icon: <CheckCircle className="h-3 w-3" />, label: 'Actif' },
      suspendu: { bg: 'bg-red-800', icon: <PauseCircle className="h-3 w-3" />, label: 'Suspendu' },
      resilie: { bg: 'bg-red-900', icon: <Ban className="h-3 w-3" />, label: 'Résilié' },
      expire: { bg: 'bg-gray-800', icon: <XCircle className="h-3 w-3" />, label: 'Expiré' },
      archive: { bg: 'bg-slate-800', icon: <Download className="h-3 w-3" />, label: 'Archivé' }
    };
    
    const badge = badges[statut] || badges.brouillon;
    return (
      <Badge className={`${badge.bg} text-white`}>
        {badge.icon}
        <span className="ml-1">{badge.label}</span>
      </Badge>
    );
  };

  const getTypeIcon = (type) => {
    if (type === 'cooper') return <Shield className="h-5 w-5 text-green-600" />;
    if (type === 'expert') return <Award className="h-5 w-5 text-purple-600" />;
    return <Users className="h-5 w-5 text-blue-600" />;
  };

  const getFilteredContrats = () => {
    let filtered = [...conventions];
    
    if (searchQuery) {
      filtered = filtered.filter(c =>
        c.titre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.numero_contrat?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.destinataire_nom?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.type_convention === typeFilter);
    }
    
    if (statutFilter !== 'all') {
      filtered = filtered.filter(c => c.statut === statutFilter);
    }
    
    return filtered;
  };

  // Utiliser les filtres avancés si disponibles, sinon les filtres basiques
  const filteredConventions = useMemo(() => {
    if (useAdvancedFilters && filteredConventionsList.length > 0) {
      return filteredConventionsList;
    }
    // Si les filtres avancés n'ont pas encore été appliqués, retourner toutes les conventions
    if (useAdvancedFilters && filteredConventionsList.length === 0 && conventions.length > 0) {
      return conventions;
    }
    return getFilteredContrats();
  }, [filteredConventionsList, useAdvancedFilters, conventions, searchQuery, typeFilter, statutFilter]);

  return {
    _activeTab, _handleWordImport, _setActiveTab, _setSearchQuery, _setStatutFilter, _setTypeFilter,
    _setUseAdvancedFilters, actionData, actionMasseData, conventions, coopersList, creatingRecipient,
    editFormData, envoiData, expertsList, fetchData, filteredConventions, filteredConventionsList,
    formData, getAuthHeader, getFilteredContrats, getStatutBadge, getTypeIcon, handleAction,
    handleActionMasse, handleCancelEdit, handleCreateConvention, handleCreateRecipientByEmail, handleDelete, handleDuplicateConvention,
    handleEnvoyerConvention, handleOpenEdit, handleSaveEdit, handleSelectAll, handleSelectConvention, isEditing,
    loading, newRecipientEmail, newRecipientEntreprise, newRecipientNom, newRecipientPrenom, openActionMasseModal,
    openActionModal, openCreateModal, openDetailModal, openEditModal, openEnvoiModal, openPaymentShareModal,
    resetActionData, resetActionMasseData, resetEnvoiData, resetForm, searchQuery, selectAll,
    selectedConvention, selectedConventions, setActionData, setActionMasseData, setContrats, setCoopersList,
    setCreatingRecipient, setEditFormData, setEnvoiData, setExpertsList, setFilteredConventionsList, setFormData,
    setIsEditing, setLoading, setNewRecipientEmail, setNewRecipientEntreprise, setNewRecipientNom, setNewRecipientPrenom,
    setOpenActionMasseModal, setOpenActionModal, setOpenCreateModal, setOpenDetailModal, setOpenEditModal, setOpenEnvoiModal,
    setOpenPaymentShareModal, setSelectAll, setSelectedContrat, setSelectedConventions, setShowCreateRecipient, setStats,
    showCreateRecipient, stats, statutFilter, typeFilter, useAdvancedFilters,
  };
}
